// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import type { InputHTMLAttributes, CSSProperties } from 'react';
import { useId } from 'react';
import { cn } from '../utils/helpers';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export function Input({ label, error, className, id, style, ...props }: InputProps) {
  const generatedId = useId();
  const inputId = id || generatedId;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-bold mb-3"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={cn(
          // Base styles - Neo-Brutalist with theme colors
          'w-full font-mono font-bold px-4 py-2.5',
          'transition-all duration-150',
          'focus:outline-none',
          className
        )}
        style={
          {
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)',
            border: error ? 'var(--neo-border-chunky)' : 'var(--neo-border-medium)',
            borderColor: error ? 'var(--color-error)' : 'var(--color-input-border)',
            boxShadow: error ? 'var(--neo-shadow-sm)' : 'var(--neo-shadow-md)',
            ...style,
          } as CSSProperties
        }
        {...props}
      />
      {error && (
        <p className="mt-2 text-sm font-bold" style={{ color: 'var(--color-error)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
